import React from 'react';
import { ColorRing } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Device } from 'api/InteractiveMap/types';
import { useHubDeleteModalStore } from 'components/DeleteHubModal/HubsDeleteStore';

interface HubPopupProps {
  hub: Device;
  onView?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
  showMoveActions?: boolean;
  onMoveAction?: (action: 'adjustment' | 'new_place') => void;
  isMoving?: boolean;
  isLoading?: boolean;
  trapsConnected: number;
  mctTraps: number;
  srbmTraps: number;
  smstTraps: number;
  srstTraps: number;
  isActiveFilterArchive?: boolean;
}

export const HubPopup: React.FC<HubPopupProps> = ({
  hub,
  onMove,
  showMoveActions,
  onMoveAction,
  isLoading,
  trapsConnected,
  mctTraps,
  srbmTraps,
  smstTraps,
  srstTraps,
  isActiveFilterArchive,
}) => {
  const { openDeleteHubModal } = useHubDeleteModalStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleDeleteClick = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    openDeleteHubModal(hub.model, hub.id, trapsConnected);
  };
  const handleMoveClick = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    onMove && onMove();
  };
  return (
    <div className="trap-popup mw-100" style={{ width: '500px' }}>
      {/* Pop-up content */}
      <div className="trap-popup-header text-purple mb-2 fs-16">
        <strong>{t('Popups.HubPopup.title', { number: hub.sequence_number })}</strong>
      </div>
      <div>{t('Popups.HubPopup.serialNumber', { slug: hub.slug })}</div>
      <div>{t('Popups.HubPopup.connectedDevices', { number: trapsConnected })}</div>
      {mctTraps !== 0 && <div className="mx-2">{t('Popups.HubPopup.mctConnected', { number: mctTraps })}</div>}
      {smstTraps !== 0 && <div className="mx-2">{t('Popups.HubPopup.smstConnected', { number: smstTraps })}</div>}
      {srstTraps !== 0 && <div className="mx-2">{t('Popups.HubPopup.srstConnected', { number: srstTraps })}</div>}
      {srbmTraps !== 0 && <div className="mx-2">{t('Popups.HubPopup.srbmConnected', { number: srbmTraps })}</div>}
      {/* Action Start Block */}
      <div className="row align-content-center text-center" title={t('Popups.MctPopup.viewHubPage')}>
        <div className="col-4 align-items-center text-center">
          <a href={`/r/hub/info/${hub.slug}/`} className="btn btn-ghost-info custom-toggle">
            <span className="icon-on">
              <i className="ri-eye-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-eye-fill align-bottom" />
            </span>
          </a>
        </div>
        <div className="col-4 align-items-center" title={t('Popups.MctPopup.moveHub')}>
          <button
            type="button"
            className="btn btn-ghost-success custom-toggle"
            data-bs-toggle="button"
            onClick={handleMoveClick}
          >
            <span className="icon-on">
              <i className="ri-drag-move-2-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-drag-move-2-fill align-bottom" />
            </span>
          </button>
        </div>
        <div className="col-4 align-items-center text-center" title={t('Popups.MctPopup.removeHub')}>
          <button
            type="button"
            id="del_hub_btn"
            className="btn btn-ghost-danger custom-toggle"
            data-bs-toggle="button"
            onClick={handleDeleteClick}
          >
            <span className="icon-on">
              <i className="ri-delete-bin-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-delete-bin-fill align-bottom" />
            </span>
          </button>
        </div>
      </div>
      {/* Action End Block */}
      {showMoveActions && (
        <div className="move-actions-row mt-2 text-center">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80px' }}>
              <ColorRing
                visible
                height="50"
                width="50"
                ariaLabel="color-ring-loading"
                wrapperStyle={{}}
                wrapperClass="color-ring-wrapper"
                colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
              />
            </div>
          ) : (
            <div className="d-grid gap-4 mt-4">
              <div className="row g-2 align-items-stretch">
                <div className="col-4">
                  <button className="move_buttons" onClick={() => onMoveAction && onMoveAction('new_place')}>
                    {t('Popups.MctPopup.newPlace')}
                  </button>
                </div>

                <div className="col col-8 d-flex flex-column justify-content-start align-items-start">
                  <div className="fw-bold">{t('Popups.MctPopup.newLocation')}</div>
                  <span className="text-start">{t('Popups.MctPopup.newLocationSubtitle')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
